import { writable } from 'svelte/store';

export type Appearance = 'system' | 'light' | 'dark';
function parseAppearance(value: unknown): Appearance {
	return value === 'light' || value === 'dark' ? value : 'system';
}

export const appearance = writable<Appearance>('system');
export const resolvedAppearance = writable<'light' | 'dark'>('light');

/** Owned by the root layout so System follows the OS even with the menu closed. */
export function observeAppearance(): () => void {
	try {
		appearance.set(parseAppearance(localStorage.getItem('theme')));
	} catch {
		/* Use system. */
	}
	const media: MediaQueryList = matchMedia('(prefers-color-scheme: dark)');
	let selected: Appearance = 'system';
	const apply = (): void => {
		const dark: boolean = selected === 'dark' || (selected === 'system' && media.matches);
		document.documentElement.classList.toggle('dark', dark);
		resolvedAppearance.set(dark ? 'dark' : 'light');
	};
	const unsubscribe: () => void = appearance.subscribe((value: Appearance) => {
		selected = value;
		apply();
	});
	const storage = (event: StorageEvent): void => {
		if (event.key === 'theme' || event.key === null)
			appearance.set(parseAppearance(event.newValue));
	};
	media.addEventListener('change', apply);
	window.addEventListener('storage', storage);
	return () => {
		unsubscribe();
		media.removeEventListener('change', apply);
		window.removeEventListener('storage', storage);
	};
}

export function setAppearance(value: Appearance): void {
	appearance.set(value);
	try {
		localStorage.setItem('theme', value);
	} catch {
		/* Memory-only preferences. */
	}
}
